import type { ReactNode } from 'react';
import { FaCheck, FaTimes } from 'react-icons/fa';
import { Badge, Button } from '@/components/UI';
import { formatApprovalTimestamp, getAIWorkApprovalBadgeVariant, humanizeAIWorkLabel } from './helpers';

/**
 * Minimal shape any AI Workbench approval must provide to render a decision row.
 * Both the central approvals inbox and the scorecard audit panel pass compatible
 * (often richer) objects.
 */
export interface AIWorkApprovalRowView {
    id: number;
    approvalType: string;
    status: string;
    requestedBy: string;
    rationale?: string | null;
    decidedAt?: string | null;
}

interface AIWorkApprovalRowProps {
    approval: AIWorkApprovalRowView;
    disabled: boolean;
    isDeciding: boolean;
    onDecide: (approvalId: number, status: 'approved' | 'rejected') => void;
    approveLabel?: string;
    /** Optional extra context (session/artifact details) rendered under the request line. */
    context?: ReactNode;
}

/**
 * Presentational, type-agnostic approval row with Approve/Reject controls. Shared
 * across every AI Workbench approval surface so the decide UX stays consistent.
 */
const AIWorkApprovalRow = ({ approval, disabled, isDeciding, onDecide, approveLabel = 'Approve', context }: AIWorkApprovalRowProps) => (
    <div className="grid gap-2 border-t border-zinc-800 py-3 first:border-t-0 md:grid-cols-[1fr_110px_auto] md:items-center">
        <div className="min-w-0">
            <div className="truncate text-sm font-medium text-white" title={humanizeAIWorkLabel(approval.approvalType)}>
                {humanizeAIWorkLabel(approval.approvalType)}
            </div>
            <div
                className="truncate text-xs text-zinc-500"
                title={`Requested by ${approval.requestedBy}${approval.rationale ? ` · ${approval.rationale}` : ''}`}
            >
                Requested by {approval.requestedBy}
                {approval.rationale ? ` · ${approval.rationale}` : ''}
            </div>
            {context ? <div className="mt-1.5">{context}</div> : null}
        </div>
        <Badge variant={getAIWorkApprovalBadgeVariant(approval.status)}>{humanizeAIWorkLabel(approval.status)}</Badge>
        {approval.status === 'requested' ? (
            <div className="flex flex-wrap gap-2 md:justify-end">
                <Button
                    disabled={disabled}
                    icon={<FaCheck />}
                    isLoading={isDeciding}
                    size="sm"
                    variant="primary"
                    onClick={() => onDecide(approval.id, 'approved')}
                >
                    {approveLabel}
                </Button>
                <Button
                    disabled={disabled}
                    icon={<FaTimes />}
                    isLoading={isDeciding}
                    size="sm"
                    variant="secondary"
                    onClick={() => onDecide(approval.id, 'rejected')}
                >
                    Reject
                </Button>
            </div>
        ) : (
            <div className="text-right text-xs text-zinc-500">
                {approval.decidedAt ? `Decided ${formatApprovalTimestamp(approval.decidedAt)}` : 'No action required'}
            </div>
        )}
    </div>
);

export default AIWorkApprovalRow;
